import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Home, Download, History } from 'lucide-react';
import Footer from '../components/Footer';
import { useSafeBack } from '../hooks/useSafeBack';
import { toast } from 'sonner';

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '');

const statusBadge = (r) => {
  if (r.participation === 'waitlisted') return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Waitlisted</span>;
  if (r.confirmation_status === 'confirmed') return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Confirmed</span>;
  if (r.confirmation_status === 'released') return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Released</span>;
  return <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">Joined</span>;
};

const PlayerHistory = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/account-settings');

  const today = new Date();
  const ago30 = new Date(today.getTime() - 30 * 86400000);
  const toISO = (d) => d.toISOString().slice(0, 10);

  const [start, setStart] = useState(toISO(ago30));
  const [end, setEnd] = useState(toISO(today));
  const [includeUpcoming, setIncludeUpcoming] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    fetchReport();
  }, []);

  const buildParams = () => ({ start, end, include_upcoming: includeUpcoming });

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/me/run-history', { params: buildParams() });
      setReport(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await apiClient.get('/me/run-history', {
        params: { ...buildParams(), format: 'csv' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-run-history_${start}_${end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  const summary = report?.summary;
  const rows = report?.rows || [];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-30 bg-white border-b border-[#E4E4E7]"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <div className="mx-auto max-w-3xl px-4 pb-4 flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={goBack} className="h-10 w-10 rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="h-8 w-8 rounded-full" data-testid="nav-home-btn" aria-label="Dashboard">
            <Home className="w-4 h-4 text-zinc-400" />
          </Button>
          <h1 className="text-xl font-bold text-zinc-900 ml-1 flex items-center gap-2">
            <History className="w-5 h-5 text-orange-500" /> My Run History
          </h1>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-6 space-y-6">
        <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="history-filters">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <Label htmlFor="start" className="text-xs text-zinc-500">Start</Label>
              <Input id="start" type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="filter-start" />
            </div>
            <div>
              <Label htmlFor="end" className="text-xs text-zinc-500">End</Label>
              <Input id="end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="filter-end" />
            </div>
            <div className="flex items-center gap-2">
              <input id="upcoming" type="checkbox" checked={includeUpcoming} onChange={(e) => setIncludeUpcoming(e.target.checked)} data-testid="filter-upcoming" className="h-4 w-4" />
              <Label htmlFor="upcoming" className="text-sm text-zinc-600">Include upcoming</Label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={fetchReport} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="apply-filters-btn">
              {loading ? 'Loading...' : 'Apply'}
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="history-summary">
            {[
              ['Sessions', summary.sessions],
              ['Rostered', summary.rostered],
              ['Waitlisted', summary.waitlisted],
              ['Confirmed', summary.confirmed_attended],
            ].map(([label, val]) => (
              <div key={label} className="bg-white rounded-xl border border-zinc-200 p-4">
                <div className="text-2xl font-bold text-zinc-900">{val ?? 0}</div>
                <div className="text-xs text-zinc-500">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-xl border border-zinc-200 divide-y divide-zinc-50" data-testid="history-list">
          {rows.length === 0 && (
            <div className="px-4 py-8 text-center text-zinc-400">No runs in this range.</div>
          )}
          {rows.map((r, i) => (
            <div key={i} className="px-4 py-3 flex items-center justify-between gap-3" data-testid="history-row">
              <div className="min-w-0">
                <div className="font-medium text-zinc-800 truncate">{r.session_title || 'Untitled run'}</div>
                <div className="text-xs text-zinc-500 truncate">
                  {fmtDate(r.date_time_utc)}{r.club_name ? ` • ${r.club_name}` : ''}{r.location_name ? ` • ${r.location_name}` : ''}
                </div>
              </div>
              <div className="shrink-0">{statusBadge(r)}</div>
            </div>
          ))}
        </div>
      </div>
      <Footer variant="slim" />
    </div>
  );
};

export default PlayerHistory;
